import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { get, put } from "helpers/api_helper"
import settingsList from "./settingsList"

const settingsByType = settingsList.reduce(
  (acc, setting) => ({ ...acc, [setting.type]: setting }),
  {},
)

const getDefaults = () =>
  settingsList.reduce(
    (acc, setting) => ({ ...acc, [setting.type]: { ...setting.defaults } }),
    {},
  )

const isPlainObject = v => v !== null && typeof v === "object" && !Array.isArray(v)

const errorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.error || fallback

function Settings() {
  document.title = "Settings"

  const [saved, setSaved] = useState(getDefaults)
  const [values, setValues] = useState(getDefaults)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const data = await get("settings")
        const merged = getDefaults()
        ;(Array.isArray(data) ? data : []).forEach(s => {
          if (merged[s.type] && isPlainObject(s.values)) {
            merged[s.type] = { ...merged[s.type], ...s.values }
          }
        })
        setSaved(merged)
        setValues(merged)
      } catch (err) {
        toast.error(errorMessage(err, "Could not load settings"))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const changedKeys = Object.keys(values).filter(
    key => JSON.stringify(values[key]) !== JSON.stringify(saved[key]),
  )
  const isDirty = changedKeys.length > 0

  const handleSave = async () => {
    setSaving(true)
    const results = await Promise.allSettled(
      changedKeys.map(key =>
        put(`settings/update/${key}`, {
          values: values[key],
          category: settingsByType[key].category,
        }),
      ),
    )

    const failed = []
    const nextSaved = { ...saved }
    results.forEach((result, i) => {
      const key = changedKeys[i]
      if (result.status === "fulfilled") nextSaved[key] = values[key]
      else failed.push({ key, err: result.reason })
    })
    setSaved(nextSaved)
    setSaving(false)

    if (failed.length) {
      toast.error(`${failed[0].key}: ${errorMessage(failed[0].err, "could not be saved")}`)
    } else {
      toast.success("Settings saved")
    }
  }

  const handleReset = () => setValues(saved)

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Settings" breadcrumbItem="Settings" />
          <Row>
            <Col xl="6" lg="8">
              <Card>
                <CardBody className="pb-1">
                  {loading ? (
                    <div className="text-center text-muted py-5">
                      <i className="bx bx-loader-alt bx-spin me-1" /> Loading settings...
                    </div>
                  ) : (
                    settingsList.map(({ type, component: SettingComponent }) => (
                      <SettingComponent
                        key={type}
                        value={values[type]}
                        onChange={value =>
                          setValues(prev => ({ ...prev, [type]: value }))
                        }
                      />
                    ))
                  )}
                </CardBody>
                <CardBody className="border-top d-flex align-items-center justify-content-between">
                  <span className="text-muted font-size-13">
                    {isDirty ? (
                      <>
                        <i className="bx bx-info-circle text-warning me-1" />
                        You have unsaved changes
                      </>
                    ) : (
                      <>
                        <i className="bx bx-check-circle text-success me-1" />
                        All changes saved
                      </>
                    )}
                  </span>
                  <div className="d-flex gap-2">
                    <Button color="light" onClick={handleReset} disabled={!isDirty || saving}>
                      Reset
                    </Button>
                    <Button
                      color="success"
                      onClick={handleSave}
                      disabled={!isDirty || saving || loading}
                    >
                      <i className={`bx ${saving ? "bx-loader-alt bx-spin" : "bx-save"} me-1`} />
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  )
}

export default Settings
